import { useEffect, useRef, useState } from "react";
import { CheckCircle2, FileUp, Globe, Loader2, XCircle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api, errMsg } from "@/lib/api";

const JobProgress = ({ job }) => {
  const pct = job.total ? Math.round((job.current / job.total) * 100) : 5;
  return (
    <div className="space-y-3" data-testid="import-progress">
      <p className="text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" />{job.step || "Starting…"}</p>
      <Progress value={pct} />
      <p className="text-xs text-[var(--muted-ink)]">{job.total ? `${job.current} / ${job.total}` : ""} · You can close this window; the import keeps running.</p>
      {job.products?.length > 0 && <p className="text-xs">Added so far: {job.products.map((p) => p.name).join(", ")}</p>}
    </div>
  );
};

const JobResult = ({ job }) => (
  <div className="space-y-4" data-testid="import-result">
    {job.status === "failed" ? (
      <p className="text-sm text-red-700 flex gap-2" data-testid="import-error"><XCircle size={16} />{job.error}</p>
    ) : (
      <p className="text-sm flex gap-2" data-testid="import-summary"><CheckCircle2 size={16} className="text-[var(--terracotta)]" />
        Added {job.products.length} products ({job.products.length * 2} pages) from {job.filename}</p>
    )}
    {job.products?.length > 0 && (
      <ul className="text-sm border border-[var(--line)] divide-y divide-[var(--line)] max-h-56 overflow-y-auto" data-testid="import-product-list">
        {job.products.map((p) => <li key={p.id} className="px-3 py-2 flex justify-between"><span><b>{p.code}</b> {p.name}</span><span className="text-xs text-[var(--muted-ink)] truncate max-w-[45%]">{p.url ? <a href={p.url} target="_blank" rel="noreferrer" className="hover:underline">{p.url.replace(/^https?:\/\/(www\.)?/, "")}{p.note ? ` · ${p.note}` : ""}</a> : <>page {p.pages?.join(", ")}{p.source_name ? ` · was “${p.source_name}”` : ""}</>}</span></li>)}
      </ul>
    )}
    {job.configs_added?.length > 0 && <p className="text-xs" data-testid="import-configs-added">New configurations added to Settings: {job.configs_added.join(", ")}</p>}
    {job.skipped?.length > 0 && <p className="text-xs text-[var(--muted-ink)]" data-testid="import-skipped">Skipped: {job.skipped.join("; ")}</p>}
  </div>
);

export const ImportCatalogDialog = ({ open, setOpen, catId, catName, jobId, setJobId, onDone }) => {
  const [job, setJob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [web, setWeb] = useState({ url: "", keyword: "", limit: 30 });
  const input = useRef(null);

  useEffect(() => {
    if (!jobId) { setJob(null); return; }
    let stop = false;
    const tick = async () => {
      let data;
      try { data = (await api.get(`/import/jobs/${jobId}`)).data; }
      catch { if (!stop) setTimeout(tick, 5000); return; }
      if (stop) return;
      setJob(data);
      onDone();
      if (!["done", "failed"].includes(data.status)) setTimeout(tick, 2500);
    };
    tick();
    return () => { stop = true; };
  }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("category_id", catId);
    fd.append("file", file);
    try { const { data } = await api.post("/import/catalog", fd); setJobId(data.id); }
    catch (e) { toast.error(errMsg(e)); }
    setUploading(false);
  };
  const finished = job && ["done", "failed"].includes(job.status);
  const startWeb = async () => {
    setUploading(true);
    try { const { data } = await api.post("/import/website", { category_id: catId, url: web.url.trim(), keyword: web.keyword, limit: Number(web.limit) || 30 }); setJobId(data.id); }
    catch (e) { toast.error(errMsg(e)); }
    setUploading(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[var(--cream)] max-w-lg" data-testid="import-dialog">
        <DialogHeader><DialogTitle>Import into {catName}</DialogTitle></DialogHeader>
        {!job ? (
          <Tabs defaultValue="file">
            <TabsList className="w-full"><TabsTrigger value="file" className="flex-1" data-testid="import-tab-file">From a file</TabsTrigger><TabsTrigger value="web" className="flex-1" data-testid="import-tab-web">From a website</TabsTrigger></TabsList>
            <TabsContent value="file" className="space-y-4">
              <p className="text-sm text-[var(--muted-ink)]">Upload any PDF or PowerPoint catalog. AI finds each product and keeps only its photos, sizes (converted to inches) and prices. Names, descriptions, materials and highlights are written fresh by AI from the photos, then 2 pages per product are added in your format.</p>
              <button onClick={() => input.current?.click()} disabled={uploading} data-testid="import-dropzone"
                className="w-full border border-dashed border-[var(--ink)]/40 py-12 flex flex-col items-center gap-2 hover:bg-[var(--paper)] transition-colors">
                {uploading ? <Loader2 className="animate-spin" /> : <FileUp />}
                <span className="text-sm font-medium">{uploading ? "Uploading…" : "Choose PDF or .pptx file"}</span>
              </button>
              <input ref={input} type="file" accept=".pdf,.pptx" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="import-file-input" />
            </TabsContent>
            <TabsContent value="web" className="space-y-3">
              <p className="text-sm text-[var(--muted-ink)]">Paste a website or collection link. The app finds every product page, then collects its photos, name, description, materials, sizes (in inches) and prices from that page.</p>
              <div><Label>Website or collection link</Label><Input value={web.url} onChange={(e) => setWeb({ ...web, url: e.target.value })} placeholder="https://www.brand.com/collections/sofas" data-testid="import-web-url" /></div>
              <div className="grid grid-cols-[1fr_120px] gap-3">
                <div><Label>Only products with these words (optional)</Label><Input value={web.keyword} onChange={(e) => setWeb({ ...web, keyword: e.target.value })} placeholder="sofa, couch" data-testid="import-web-keyword" /></div>
                <div><Label>Max products</Label><Input type="number" min="1" max="100" value={web.limit} onChange={(e) => setWeb({ ...web, limit: e.target.value })} data-testid="import-web-limit" /></div>
              </div>
              <Button className="w-full" disabled={!web.url.trim() || uploading} onClick={startWeb} data-testid="import-web-start">
                {uploading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Globe size={14} className="mr-2" />}Crawl website and add products</Button>
            </TabsContent>
          </Tabs>
        ) : finished ? <JobResult job={job} /> : <JobProgress job={job} />}
        {finished && <Button variant="outline" onClick={() => setJobId(null)} data-testid="import-another-button">Import another catalog</Button>}
      </DialogContent>
    </Dialog>
  );
};
